/**
 * Calendar Event Detail Page
 * Full event view with all details and RSVP controls
 */

import { useMemo } from 'react'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
} from '@mui/material'
import {
  CalendarMonth,
  AccessTime,
  LocationOn,
  People,
  Edit,
  Share,
  ArrowBack,
  Public,
  Lock,
  Map,
  Link as LinkIcon,
} from '@mui/icons-material'
import { useCalendarEvent } from '../hooks/useCalendarEvents'
import {
  getEventStatusLabel,
  getEventStatusColor,
  CalendarEventStatus,
  EventOwnerEntityType,
} from '@api/types/calendarEvent'
import RsvpButtonGroup from '../components/RsvpButtonGroup'
import AttendeeList from '../components/AttendeeList'
import { isEventPast } from '../types/calendar-event-editor.types'
import { useAuthStore } from '@store/authStore'

/**
 * Format date for display
 */
function formatEventDateTime(
  start: string,
  end: string,
  timezone?: string,
  isAllDay?: boolean
): { dateStr: string; timeStr: string } {
  const startDate = new Date(start)
  const endDate = new Date(end)

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }

  if (timezone) {
    dateOptions.timeZone = timezone
    timeOptions.timeZone = timezone
  }

  const dateStr = startDate.toLocaleDateString(undefined, dateOptions)

  if (isAllDay) {
    if (startDate.toDateString() === endDate.toDateString()) {
      return { dateStr, timeStr: 'All day' }
    }
    return {
      dateStr: `${startDate.toLocaleDateString(undefined, dateOptions)} - ${endDate.toLocaleDateString(undefined, dateOptions)}`,
      timeStr: 'All day',
    }
  }

  const startTime = startDate.toLocaleTimeString(undefined, timeOptions)
  const endTime = endDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

  return { dateStr, timeStr: `${startTime} - ${endTime}` }
}

export function CalendarEventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()

  // Get return URL from query params, default to /events
  const returnTo = searchParams.get('returnTo') || '/events'

  const { data: event, isLoading, isError } = useCalendarEvent(eventId || '')

  // TODO: Get actual auth state
  const isLoggedIn = true
  const handleLoginRequired = () => navigate('/login')

  // Compute canEdit with fallback logic when API doesn't return canEdit
  const canEditEvent = useMemo(() => {
    if (!event) return false

    // If API explicitly returns canEdit, use it
    if (event.canEdit !== undefined) {
      return event.canEdit
    }

    // Fallback: check if current user is owner or creator
    if (!user) return false

    // Check if user is the owner (for user-owned events)
    if (
      event.owner?.entityType === EventOwnerEntityType.User &&
      event.owner?.entityId === user.userId
    ) {
      return true
    }

    // Check if user is the creator
    if (event.creator?.userId === user.userId) {
      return true
    }

    return false
  }, [event, user])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !event) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Event not found or you don't have permission to view it.
        </Alert>
        <Button
          component={Link}
          to={returnTo}
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Back
        </Button>
      </Container>
    )
  }

  const isPast = isEventPast(event.endDateTime)
  const isCancelled = event.status === CalendarEventStatus.Cancelled
  const { dateStr, timeStr } = formatEventDateTime(
    event.startDateTime,
    event.endDateTime,
    event.timezone,
    event.isAllDay
  )

  return (
    <Box>
      {/* Cover Image */}
      {event.coverImageUrl && (
        <Box
          sx={{
            width: '100%',
            height: { xs: 200, sm: 300, md: 400 },
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <CardMedia
            component="img"
            image={event.coverImageUrl}
            alt={event.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: isCancelled || isPast ? 'grayscale(50%)' : 'none',
            }}
          />
          {(isCancelled || isPast) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.3)',
              }}
            >
              <Chip
                label={isCancelled ? 'CANCELLED' : 'PAST EVENT'}
                color={isCancelled ? 'error' : 'default'}
                sx={{ fontSize: '1.5rem', py: 3, px: 4 }}
              />
            </Box>
          )}
        </Box>
      )}

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Back button */}
        <Button
          component={Link}
          to={returnTo}
          startIcon={<ArrowBack />}
          sx={{ mb: 2 }}
          color="inherit"
        >
          Back
        </Button>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
          {/* Main Content */}
          <Box sx={{ flex: 1 }}>
            {/* Status badges */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={getEventStatusLabel(event.status)}
                color={getEventStatusColor(event.status)}
                size="small"
              />
              {event.isPublic ? (
                <Chip icon={<Public />} label="Public" size="small" variant="outlined" />
              ) : (
                <Chip icon={<Lock />} label="Private" size="small" variant="outlined" />
              )}
              {event.isEventFull && (
                <Chip label="Event Full" color="warning" size="small" />
              )}
            </Stack>

            {/* Title */}
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{ textDecoration: isCancelled ? 'line-through' : 'none' }}
            >
              {event.title}
            </Typography>

            {/* Quick description */}
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              {event.quickDescription}
            </Typography>

            {/* Date/Time */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <CalendarMonth color="primary" fontSize="large" />
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {dateStr}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <AccessTime sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {timeStr}
                    {event.timezone && ` (${event.timezone})`}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Location */}
            {event.location?.name && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <LocationOn color="primary" fontSize="large" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {event.location.name}
                    </Typography>
                    {event.location.latitude && event.location.longitude && (
                      <Button
                        component="a"
                        href={`https://www.google.com/maps?q=${event.location.latitude},${event.location.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        startIcon={<Map />}
                        sx={{ mt: 1 }}
                      >
                        View on Map
                      </Button>
                    )}
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Full Description */}
            {event.fullDescription && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  About this event
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{ __html: event.fullDescription }}
                />
              </Box>
            )}

            {/* Linked Adventure */}
            {event.linkedAdventure && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <LinkIcon color="primary" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Linked Adventure
                    </Typography>
                    <Typography
                      component={Link}
                      to={`/adventures/${event.linkedAdventure.adventureId}`}
                      variant="body1"
                      fontWeight="medium"
                      sx={{ textDecoration: 'none', color: 'primary.main' }}
                    >
                      {event.linkedAdventure.title}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Tags */}
            {event.tags && event.tags.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tags
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {event.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" />
                  ))}
                </Stack>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Attendees */}
            <Typography variant="h6" gutterBottom>
              Attendees
            </Typography>
            <AttendeeList
              eventId={event.id}
              attendingCount={event.attendanceStats.attendingCount}
              tentativeCount={event.attendanceStats.tentativeCount}
              notAttendingCount={event.attendanceStats.notAttendingCount}
              variant="full"
            />
          </Box>

          {/* Sidebar */}
          <Box sx={{ width: { xs: '100%', md: 320 } }}>
            <Paper sx={{ p: 3, position: 'sticky', top: 80 }}>
              {/* RSVP Section */}
              <Typography variant="h6" gutterBottom>
                RSVP
              </Typography>
              <RsvpButtonGroup
                eventId={event.id}
                eventStatus={event.status}
                endDateTime={event.endDateTime}
                isEventFull={event.isEventFull}
                currentRsvp={event.userRsvp}
                isLoggedIn={isLoggedIn}
                onLoginRequired={handleLoginRequired}
              />

              <Divider sx={{ my: 3 }} />

              {/* Attendance Stats */}
              <Stack spacing={1} sx={{ mb: 3 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Attending
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {event.attendanceStats.attendingCount}
                    {event.maxAttendees && ` / ${event.maxAttendees}`}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Maybe
                  </Typography>
                  <Typography variant="body2">
                    {event.attendanceStats.tentativeCount}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Can't Go
                  </Typography>
                  <Typography variant="body2">
                    {event.attendanceStats.notAttendingCount}
                  </Typography>
                </Stack>
              </Stack>

              <Divider sx={{ my: 3 }} />

              {/* Organizer */}
              {event.owner && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Organized by
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Avatar src={event.owner.entityAvatarUrl}>
                      {event.owner.entityName?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="body1" fontWeight="medium">
                      {event.owner.entityName}
                    </Typography>
                  </Stack>
                </>
              )}

              {event.creator && event.owner && event.creator.userId !== event.owner.entityId && (
                <>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Created by
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={event.creator.profileImageUrl} sx={{ width: 24, height: 24 }}>
                      {event.creator.displayName.charAt(0)}
                    </Avatar>
                    <Typography variant="body2">
                      {event.creator.displayName}
                    </Typography>
                  </Stack>
                </>
              )}

              {/* Actions */}
              {canEditEvent && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Stack spacing={1}>
                    <Button
                      component={Link}
                      to={`/events/${event.id}/edit`}
                      variant="outlined"
                      startIcon={<Edit />}
                      fullWidth
                    >
                      Edit Event
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<Share />}
                      fullWidth
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href)
                        // TODO: Show snackbar
                      }}
                    >
                      Share Event
                    </Button>
                  </Stack>
                </>
              )}
            </Paper>
          </Box>
        </Stack>
      </Container>
    </Box>
  )
}

export default CalendarEventDetailPage
